import React, { useState, useEffect } from 'react';
import { usePortvilleBillingView } from '../../../hooks/usePortvilleBillingView';
import { PortvilleBill } from '../../../types/portvilleBilling';

const PortvilleBillingAuthPage: React.FC = () => {
  const { 
    bills, 
    loading, 
    error, 
    loadBillsByRoom,
    verifyRoomPhoneNumber,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
    setLoading
  } = usePortvilleBillingView();

  // 입력 상태
  const [roomNumber, setRoomNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerificationRequired, setIsVerificationRequired] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  // 현재 청구서 상태 (향후 사용 예정)
  // const [currentBill, setCurrentBill] = useState<PortvilleBill | null>(null);

  // 현재 연/월로 초기화
  useEffect(() => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
  }, [setSelectedYear, setSelectedMonth]);

  // 현재 월 청구서 찾기 (향후 사용 예정)
  // useEffect(() => {
  //   if (bills.length > 0) {
  //     const currentMonthBill = bills.find(bill => 
  //       bill.billingPeriod.year === selectedYear && 
  //       bill.billingPeriod.month === selectedMonth
  //     );
  //     setCurrentBill(currentMonthBill || null);
  //   } else {
  //     setCurrentBill(null);
  //   }
  // }, [bills, selectedYear, selectedMonth]);

  // 전화번호 검증 및 SMS 인증 요청
  const handlePhoneVerification = async () => {
    if (!roomNumber.trim() || !phoneNumber.trim()) {
      alert('호실번호와 전화번호를 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      setVerificationError('');
      
      // 호실의 실제 전화번호와 입력한 전화번호 검증
      const isPhoneValid = await verifyRoomPhoneNumber(roomNumber, phoneNumber, selectedYear, selectedMonth);
      
      if (!isPhoneValid) {
        alert('입력하신 전화번호가 해당 호실의 등록된 전화번호와 일치하지 않습니다.\n다시 확인해주세요.');
        setLoading(false);
        return;
      }
      
      console.log('전화번호 검증 성공:', { roomNumber, phoneNumber });
      
      setIsVerificationRequired(true);
      
      // 가상 SMS 발송 시뮬레이션
      alert('인증번호가 발송되었습니다. (테스트용: 123456)');
    } catch (error) {
      console.error('전화번호 검증 실패:', error);
      alert('전화번호 검증 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // SMS 인증 코드 검증
  const handleVerificationSubmit = () => {
    if (!verificationCode.trim()) {
      setVerificationError('인증번호를 입력해주세요.');
      return;
    }

    // 가상 인증: 123456 또는 000000 입력 시 통과
    if (verificationCode === '123456' || verificationCode === '000000') {
      setIsVerified(true);
      setVerificationError('');
      
      // 청구서 조회
      loadBillsByRoom(roomNumber, selectedYear, selectedMonth);
    } else {
      setVerificationError('인증번호가 올바르지 않습니다. (테스트용: 123456 또는 000000)');
    }
  };

  // 인증 초기화
  const handleReset = () => {
    setIsVerificationRequired(false);
    setIsVerified(false);
    setVerificationCode('');
    setVerificationError('');
    setRoomNumber('');
    setPhoneNumber('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '미납';
      case 'paid': return '납부완료';
      case 'overdue': return '연체';
      case 'cancelled': return '취소';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'paid': return 'text-green-600 bg-green-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">포트빌 관리비 청구서 조회</h1>
          <p className="text-gray-600">호실번호와 전화번호를 입력하여 청구서를 조회하실 수 있습니다.</p>
        </div>

        {!isVerified ? (
          /* 인증 폼 */
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">인증 정보 입력</h2>
            
            <div className="space-y-4">
              {/* 연/월 선택 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">조회 연도</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}년</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">조회 월</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 호실번호 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">호실번호</label>
                <input
                  type="text"
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value)}
                  placeholder="예: 101, 201, 301"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isVerificationRequired}
                />
              </div>

              {/* 전화번호 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="예: 010-1234-5678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isVerificationRequired}
                />
              </div>

              {/* SMS 인증 */}
              {isVerificationRequired && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">SMS 인증번호</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="인증번호 6자리"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleVerificationSubmit}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      인증
                    </button>
                  </div>
                  {verificationError && (
                    <p className="mt-1 text-sm text-red-600">{verificationError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    테스트용: 123456 또는 000000 입력
                  </p>
                </div>
              )}

              {/* 버튼들 */}
              <div className="flex gap-2 pt-4">
                {!isVerificationRequired ? (
                  <button
                    onClick={handlePhoneVerification}
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '검증 중...' : '인증번호 발송'}
                  </button>
                ) : (
                  <button
                    onClick={handleReset}
                    className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    다시 시작
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* 청구서 조회 결과 */
          <div className="space-y-6">
            {/* 인증 완료 알림 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-green-600 text-lg mr-2">✓</div>
                <div>
                  <h3 className="text-green-800 font-medium">인증이 완료되었습니다</h3>
                  <p className="text-green-700 text-sm">
                    {roomNumber}호 {phoneNumber} - {selectedYear}년 {selectedMonth}월 청구서
                  </p>
                </div>
                <button
                  onClick={handleReset}
                  className="ml-auto px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  다른 호실 조회
                </button>
              </div>
            </div>

            {/* 로딩 상태 */}
            {loading && (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">청구서를 불러오는 중...</p>
              </div>
            )}

            {/* 에러 상태 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="text-red-600 text-lg mr-2">⚠️</div>
                  <div>
                    <h3 className="text-red-800 font-medium">오류가 발생했습니다</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* 청구서 목록 */}
            {!loading && !error && bills.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">청구서 목록</h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {bills.map((bill) => (
                    <div key={bill.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {bill.roomNumber}호 - {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
                          </h3>
                          <p className="text-sm text-gray-500">
                            발행일: {bill.issuedDate.toLocaleDateString('ko-KR')} | 
                            납부기한: {bill.dueDate.toLocaleDateString('ko-KR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(bill.totalAmount)}
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bill.status)}`}>
                            {getStatusText(bill.status)}
                          </span>
                        </div>
                      </div>

                      {/* 청구서 상세 정보 */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">기본 요금</h4>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span>월 임대료</span>
                              <span>{formatCurrency(bill.billItems.monthlyRent)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>관리비</span>
                              <span>{formatCurrency(bill.billItems.managementFee)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>관리비 부가세</span>
                              <span>{formatCurrency(bill.billItems.managementFeeVAT)}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">공과금</h4>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span>전기요금</span>
                              <span>{formatCurrency(bill.billItems.electricityFee)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>전기요금 부가세</span>
                              <span>{formatCurrency(bill.billItems.electricityFeeVAT)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>전력기금</span>
                              <span>{formatCurrency(bill.billItems.electricityFund)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>수도요금</span>
                              <span>{formatCurrency(bill.billItems.waterFee)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* 추가 요금 정보 */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <h4 className="font-medium text-gray-700 mb-2">기타 요금</h4>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span>통신요금</span>
                                <span>{formatCurrency(bill.billItems.communicationFee)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>통신요금 부가세</span>
                                <span>{formatCurrency(bill.billItems.communicationFeeVAT)}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 mb-2">상계 처리</h4>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span>상계처리비</span>
                                <span>{formatCurrency(bill.billItems.offsetProcessingFee)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>상계처리비 부가세</span>
                                <span>{formatCurrency(bill.billItems.offsetProcessingFeeVAT)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 청구서 없음 */}
            {!loading && !error && bills.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <div className="text-gray-400 text-6xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">청구서가 없습니다</h3>
                <p className="text-gray-600">
                  {selectedYear}년 {selectedMonth}월에 해당하는 청구서가 없습니다.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortvilleBillingAuthPage;
